"use client";

import { c as _c } from "react/compiler-runtime";
import type { PublicTournamentReferenceData, TournamentListItem } from "@clutch/auth/actions/tournaments";
import type { UserSportProfileSettings } from "@clutch/auth/actions/users/profile";
import { CancelCircleIcon, Search01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMemo, useState } from "react";
import { TournamentCard } from "@/components/cards/tournament-card";
import { Button } from "@/components/ui/button";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, useComboboxAnchor } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { TournamentListEmpty } from "./tournament-list-empty";
function FilterChip(t0) {
  const $ = _c(9);
  const {
    label,
    onClear
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="max-w-[160px] truncate font-medium">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `Remove ${label} filter`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HugeiconsIcon icon={CancelCircleIcon} className="h-3.5 w-3.5" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onClear || $[4] !== t2) {
    t4 = <button type="button" aria-label={t2} onClick={onClear} className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground">{t3}</button>;
    $[3] = onClear;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <span className="inline-flex h-7 items-center gap-1 rounded-full border border-border/70 bg-accent/40 pr-1 pl-2.5 text-xs">{t1}{t4}</span>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
type TournamentDiscoveryFeedProps = {
  tournaments: TournamentListItem[];
  referenceData: PublicTournamentReferenceData;
  initialDistrictSlug?: string | null;
  initialDistrictLabel?: string | null;
  viewerSportProfiles?: UserSportProfileSettings[] | null;
};

// TODO: extend this feed with default gender and age filters derived from the
// viewer profile using the same initial-chip pattern used here for skill and
// district. Player gender/DOB rarely change, so they can be wired identically
// once the product decides on the UX copy. Keep this PR scoped to skill.
export function matchesViewerSkill(tournament: TournamentListItem, profilesBySport: Map<string, UserSportProfileSettings>): boolean {
  if (tournament.skillRestrictions.length === 0) return true;
  const viewerProfile = profilesBySport.get(tournament.sportSlug);
  if (!viewerProfile || !viewerProfile.skillScaleSlug || !viewerProfile.skillLevelSlug) {
    return false;
  }
  return tournament.skillRestrictions.some(group => group.skillScaleSlug === viewerProfile.skillScaleSlug && group.levels.some(lvl => lvl.slug === viewerProfile.skillLevelSlug));
}
export function getTournamentDistrictLabel(slug: string, districtLabelsBySlug: ReadonlyMap<string, string>, options?: {
  initialDistrictSlug?: string | null;
  initialDistrictLabel?: string | null;
}) {
  const mappedLabel = districtLabelsBySlug.get(slug);
  if (mappedLabel) return mappedLabel;
  if (options?.initialDistrictSlug === slug && options.initialDistrictLabel && options.initialDistrictLabel.trim() !== "") {
    return options.initialDistrictLabel;
  }
  return slug;
}
export function TournamentDiscoveryFeed(t0) {
  const $ = _c(98);
  const {
    tournaments,
    referenceData,
    initialDistrictSlug,
    initialDistrictLabel,
    viewerSportProfiles
  } = t0;
  const [search, setSearch] = useState("");
  const [districtFilter, setDistrictFilter] = useState(initialDistrictSlug ?? "");
  const [sportFilter, setSportFilter] = useState("");
  let map;
  if ($[0] !== viewerSportProfiles) {
    map = new Map();
    for (const profile of viewerSportProfiles ?? []) {
      map.set(profile.sportSlug, profile);
    }
    $[0] = viewerSportProfiles;
    $[1] = map;
  } else {
    map = $[1];
  }
  const profilesBySport = map;
  let t1;
  if ($[2] !== viewerSportProfiles) {
    t1 = viewerSportProfiles ?? [];
    $[2] = viewerSportProfiles;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = t1.some(_temp);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const hasViewerSkillProfile = t2;
  const [skillFilterEnabled, setSkillFilterEnabled] = useState(hasViewerSkillProfile);
  const districtAnchor = useComboboxAnchor();
  const sportAnchor = useComboboxAnchor();
  let t3;
  if ($[6] !== referenceData.sports) {
    t3 = slug => referenceData.sports.find(s => s.slug === slug)?.name ?? slug;
    $[6] = referenceData.sports;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const sportLabel = t3;
  let t4;
  if ($[8] !== referenceData.districts) {
    t4 = referenceData.districts.map(_temp2);
    $[8] = referenceData.districts;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const districtOptions = t4;
  let t5;
  if ($[10] !== districtOptions) {
    t5 = new Map(districtOptions.map(_temp3));
    $[10] = districtOptions;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const districtLabelsBySlug = t5;
  let t6;
  if ($[12] !== referenceData.sports) {
    t6 = referenceData.sports.map(_temp4);
    $[12] = referenceData.sports;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const sportOptions = t6;
  let t7;
  if ($[14] !== districtOptions) {
    t7 = districtOptions.map(_temp5);
    $[14] = districtOptions;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const districtLabels = t7;
  let t8;
  if ($[16] !== sportOptions) {
    t8 = sportOptions.map(_temp6);
    $[16] = sportOptions;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const sportLabels = t8;
  let t9;
  if ($[18] !== districtLabelsBySlug || $[19] !== initialDistrictLabel || $[20] !== initialDistrictSlug) {
    t9 = slug_0 => getTournamentDistrictLabel(slug_0, districtLabelsBySlug, {
      initialDistrictSlug,
      initialDistrictLabel
    });
    $[18] = districtLabelsBySlug;
    $[19] = initialDistrictLabel;
    $[20] = initialDistrictSlug;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const districtLabel = t9;
  let t10;
  if ($[22] !== districtFilter || $[23] !== districtLabel) {
    t10 = districtFilter ? districtLabel(districtFilter) : "";
    $[22] = districtFilter;
    $[23] = districtLabel;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const selectedDistrictLabel = t10;
  let t11;
  if ($[25] !== sportFilter || $[26] !== sportLabel) {
    t11 = sportFilter ? sportLabel(sportFilter) : "";
    $[25] = sportFilter;
    $[26] = sportLabel;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const selectedSportLabel = t11;
  let t12;
  if ($[28] !== districtFilter || $[29] !== districtLabelsBySlug || $[30] !== initialDistrictLabel || $[31] !== initialDistrictSlug || $[32] !== profilesBySport || $[33] !== search || $[34] !== skillFilterEnabled || $[35] !== sportFilter || $[36] !== tournaments) {
    const q = search.trim().toLowerCase();
    t12 = tournaments.filter(t => {
      if (districtFilter && t.districtSlug !== districtFilter) {
        return false;
      }
      if (sportFilter && t.sportSlug !== sportFilter) {
        return false;
      }
      if (skillFilterEnabled && !matchesViewerSkill(t, profilesBySport)) {
        return false;
      }
      if (q) {
        const districtStr = t.districtSlug ? getTournamentDistrictLabel(t.districtSlug, districtLabelsBySlug, {
          initialDistrictSlug,
          initialDistrictLabel
        }) : "";
        const haystack = `${t.name} ${t.sportName} ${t.categoryName ?? ""} ${t.primaryVenueName ?? ""} ${districtStr} ${t.publicCode}`.toLowerCase();
        if (!haystack.includes(q)) {
          return false;
        }
      }
      return true;
    });
    $[28] = districtFilter;
    $[29] = districtLabelsBySlug;
    $[30] = initialDistrictLabel;
    $[31] = initialDistrictSlug;
    $[32] = profilesBySport;
    $[33] = search;
    $[34] = skillFilterEnabled;
    $[35] = sportFilter;
    $[36] = tournaments;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const filtered = t12;
  const hasFilters = Boolean(search) || Boolean(districtFilter) || Boolean(sportFilter) || skillFilterEnabled;
  let t13;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <HugeiconsIcon icon={Search01Icon} className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e => setSearch(e.target.value);
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== search) {
    t15 = <div className="relative flex-1">{t13}<Input placeholder="Search tournaments by name, sport, venue, or code" value={search} onChange={t14} className="h-10 rounded-xl border border-input bg-background pl-9 dark:border-transparent dark:bg-input/50" /></div>;
    $[40] = search;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== districtOptions) {
    t16 = v => {
      const label = v ?? "";
      const match = districtOptions.find(d_1 => d_1.label === label);
      setDistrictFilter(match?.slug ?? "");
    };
    $[42] = districtOptions;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ComboboxInput placeholder="District" className="h-10 rounded-xl border border-input bg-background dark:border-transparent dark:bg-input/50" showClear={true} />;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== districtAnchor) {
    t18 = <div ref={districtAnchor} className="w-full">{t17}</div>;
    $[45] = districtAnchor;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  let t20;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ComboboxEmpty className="py-6 text-center text-muted-foreground text-sm">No districts found.</ComboboxEmpty>;
    t20 = <ComboboxList className="max-h-56">{_temp8}</ComboboxList>;
    $[47] = t19;
    $[48] = t20;
  } else {
    t19 = $[47];
    t20 = $[48];
  }
  let t21;
  if ($[49] !== districtAnchor) {
    t21 = <ComboboxContent anchor={districtAnchor} className="rounded-2xl border-none bg-popover px-1 py-1.5 shadow-2xl">{t19}{t20}</ComboboxContent>;
    $[49] = districtAnchor;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== districtLabels || $[52] !== selectedDistrictLabel || $[53] !== t16 || $[54] !== t18 || $[55] !== t21) {
    t22 = <div className="flex-1 sm:w-56 sm:flex-initial"><Combobox value={selectedDistrictLabel} onValueChange={t16} items={districtLabels} itemToStringValue={_temp7} autoHighlight={true}>{t18}{t21}</Combobox></div>;
    $[51] = districtLabels;
    $[52] = selectedDistrictLabel;
    $[53] = t16;
    $[54] = t18;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== sportOptions) {
    t23 = v_0 => {
      const label_1 = v_0 ?? "";
      const match_0 = sportOptions.find(s_2 => s_2.label === label_1);
      setSportFilter(match_0?.slug ?? "");
    };
    $[57] = sportOptions;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ComboboxInput placeholder="Sport" className="h-10 rounded-xl border border-input bg-background dark:border-transparent dark:bg-input/50" showClear={true} />;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== sportAnchor) {
    t25 = <div ref={sportAnchor} className="w-full">{t24}</div>;
    $[60] = sportAnchor;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  let t27;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ComboboxEmpty className="py-6 text-center text-muted-foreground text-sm">No sports found.</ComboboxEmpty>;
    t27 = <ComboboxList className="max-h-56">{_temp10}</ComboboxList>;
    $[62] = t26;
    $[63] = t27;
  } else {
    t26 = $[62];
    t27 = $[63];
  }
  let t28;
  if ($[64] !== sportAnchor) {
    t28 = <ComboboxContent anchor={sportAnchor} className="rounded-2xl border-none bg-popover px-1 py-1.5 shadow-2xl">{t26}{t27}</ComboboxContent>;
    $[64] = sportAnchor;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== selectedSportLabel || $[67] !== sportLabels || $[68] !== t23 || $[69] !== t25 || $[70] !== t28) {
    t29 = <div className="flex-1 sm:w-44 sm:flex-initial"><Combobox value={selectedSportLabel} onValueChange={t23} items={sportLabels} itemToStringValue={_temp9} autoHighlight={true}>{t25}{t28}</Combobox></div>;
    $[66] = selectedSportLabel;
    $[67] = sportLabels;
    $[68] = t23;
    $[69] = t25;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t22 || $[73] !== t29) {
    t30 = <div className="flex gap-2">{t22}{t29}</div>;
    $[72] = t22;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t15 || $[76] !== t30) {
    t31 = <div className="flex flex-col gap-2 sm:flex-row sm:items-center">{t15}{t30}</div>;
    $[75] = t15;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  const t32 = filtered.length === tournaments.length ? `${tournaments.length} ${tournaments.length === 1 ? "tournament" : "tournaments"}` : `${filtered.length} of ${tournaments.length} tournaments`;
  let t33;
  if ($[78] !== t32) {
    t33 = <span className="text-muted-foreground text-sm">{t32}</span>;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== districtFilter || $[81] !== districtLabel || $[82] !== hasFilters || $[83] !== search || $[84] !== skillFilterEnabled || $[85] !== sportFilter || $[86] !== sportLabel) {
    t34 = hasFilters && <><span aria-hidden={true} className="text-muted-foreground/40">·</span><div className="flex flex-wrap items-center gap-1.5">{search && <FilterChip label={`"${search}"`} onClear={() => setSearch("")} />}{districtFilter && <FilterChip label={districtLabel(districtFilter)} onClear={() => setDistrictFilter("")} />}{sportFilter && <FilterChip label={sportLabel(sportFilter)} onClear={() => setSportFilter("")} />}{skillFilterEnabled && <FilterChip label="Matching your skill levels" onClear={() => setSkillFilterEnabled(false)} />}<Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground text-xs hover:text-foreground" onClick={() => {
          setSearch("");
          setDistrictFilter("");
          setSportFilter("");
          setSkillFilterEnabled(false);
        }}>Clear all</Button></div></>;
    $[80] = districtFilter;
    $[81] = districtLabel;
    $[82] = hasFilters;
    $[83] = search;
    $[84] = skillFilterEnabled;
    $[85] = sportFilter;
    $[86] = sportLabel;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== t33 || $[89] !== t34) {
    t35 = <div className="flex flex-wrap items-center gap-2">{t33}{t34}</div>;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== filtered || $[92] !== hasFilters) {
    t36 = filtered.length === 0 ? <TournamentListEmpty isFiltered={hasFilters} /> : <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{filtered.map(_temp11)}</div>;
    $[91] = filtered;
    $[92] = hasFilters;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== t31 || $[95] !== t35 || $[96] !== t36) {
    t37 = <div className="space-y-4">{t31}{t35}{t36}</div>;
    $[94] = t31;
    $[95] = t35;
    $[96] = t36;
    $[97] = t37;
  } else {
    t37 = $[97];
  }
  return t37;
}
function _temp11(t_0) {
  return <TournamentCard key={t_0.id} tournament={t_0} />;
}
function _temp10(label_2) {
  return <ComboboxItem key={label_2} value={label_2} className="rounded-xl px-4 py-2 text-sm">{label_2}</ComboboxItem>;
}
function _temp9(item_0) {
  return item_0;
}
function _temp8(label_0) {
  return <ComboboxItem key={label_0} value={label_0} className="rounded-xl px-4 py-2 text-sm">{label_0}</ComboboxItem>;
}
function _temp7(item) {
  return item;
}
function _temp6(s_1) {
  return s_1.label;
}
function _temp5(d_0) {
  return d_0.label;
}
function _temp4(s_0) {
  return {
    slug: s_0.slug,
    label: s_0.name
  };
}
function _temp3(district) {
  return [district.slug, district.label];
}
function _temp2(d) {
  return {
    slug: d.slug,
    label: `${d.district}, ${d.state}`
  };
}
function _temp(p) {
  return p.skillScaleSlug && p.skillLevelSlug;
}